import React, { useMemo, useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { CL_CHOSE_NOT_TO_SEND } from '../../comm-log/mockData';
import { CL_DISPOSITIONS, CL_RANGES, reasonSlug } from '../../comm-log/types';
import type {
  ClAgent,
  ClChannel,
  ClDirection,
  ClDisposition,
  ClEmptyState,
  ClRange,
  ClRow,
  ClVerdict,
  CommLogData,
} from '../../comm-log/types';
import { CommLogDetailDrawer } from './CommLogDetailDrawer';
import { languageName } from '../../platform-languages';

export interface CommLogViewProps {
  data: CommLogData;
  /** Deep-link target: open pre-filtered to one agent (roster "See everything it sent", §3.1). */
  initialAgent?: ClAgent;
  /**
   * The filters, read from the URL (spec 18 §5). **Every filter round-trips** —
   * without this the Dashboard's promise that *every number opens the rows behind
   * it* stays theoretical: a tile click has to be a link, not a sequence of clicks
   * the user performs on arrival.
   */
  filters?: ClFilters;
  onFiltersChange?: (next: ClFilters) => void;
  /** Distinguishes *the filter matched nothing* from *the query could not run*. */
  empty?: ClEmptyState;
}

export interface ClFilters {
  direction: 'all' | ClDirection;
  agent: 'all' | ClAgent;
  channel: 'all' | ClChannel;
  /** Carries whichever vocabulary `direction` implies — the six outbound verdicts,
   * the composite, or one of the seven inbound dispositions. */
  verdict: VerdictFilter;
  /** An appointment-reason slug, or the literal `all`. */
  reason: string;
  range: ClRange;
  query: string;
}

export type VerdictFilter = 'all' | ClVerdict | 'chose-not' | ClDisposition;

export const CL_DEFAULT_FILTERS: ClFilters = {
  direction: 'all',
  agent: 'all',
  channel: 'all',
  verdict: 'all',
  reason: 'all',
  range: 'today',
  query: '',
};

const DISPOSITION_IDS = CL_DISPOSITIONS.map((d) => d.id);
const isDisposition = (v: string): v is ClDisposition => (DISPOSITION_IDS as string[]).includes(v);
const VERDICT_IDS: ClVerdict[] = ['sent', 'held', 'skipped', 'blocked', 'suppressed', 'failed'];
const isVerdict = (v: string): v is ClVerdict => (VERDICT_IDS as string[]).includes(v);

/**
 * `direction` and `verdict` must agree (§5). `direction=inbound&verdict=sent` is
 * impossible — **drop the verdict, keep the direction**, rather than returning an
 * empty table that looks like a real zero.
 */
export function reconcileFilters(f: ClFilters): ClFilters {
  if (f.direction === 'inbound' && f.verdict !== 'all' && !isDisposition(f.verdict)) {
    return { ...f, verdict: 'all' };
  }
  if (f.direction === 'outbound' && f.verdict !== 'all' && f.verdict !== 'chose-not' && !isVerdict(f.verdict)) {
    return { ...f, verdict: 'all' };
  }
  return f;
}

/** A row's own direction. Outbound unless it says otherwise. */
const dirOf = (r: ClRow): ClDirection => r.direction ?? 'outbound';

const AGENT_ORDER: ClAgent[] = ['receptionist', 'sms', 'email', 'gap-filler', 'treatment', 'invisalign', 'ar', 'recall', 'reminders'];

const AGENT_LABEL: Record<ClAgent, string> = {
  receptionist: 'AI Receptionist',
  sms: 'AI SMS',
  email: 'AI Email',
  reminders: 'Appointment Reminders',
  recall: 'Recall',
  'gap-filler': 'Gap Filler',
  treatment: 'Treatment Coordinator',
  invisalign: 'Invisalign',
  ar: 'Accounts receivable',
};

const AGENT_DOT: Record<ClAgent, string> = {
  receptionist: 'var(--brand-indigo)',
  sms: 'var(--brand-indigo)',
  email: 'var(--brand-indigo)',
  reminders: 'var(--brand-indigo)',
  recall: 'var(--money-green)',
  'gap-filler': 'var(--warn-amber-bar)',
  treatment: 'var(--violet)',
  invisalign: 'var(--brand-indigo)',
  ar: 'var(--violet)',
};

const CHANNEL_ICON: Record<ClChannel, IconKind> = { text: 'chat', email: 'mail', call: 'phone' };

/** A soft per-channel chip so the row has a leading anchor and channels scan at a glance. */
const CHANNEL_CHIP: Record<ClChannel, { fg: string; bg: string }> = {
  text: { fg: 'var(--brand-indigo)', bg: 'var(--brand-indigo-tint)' },
  email: { fg: 'var(--violet)', bg: 'var(--violet-tint)' },
  call: { fg: 'var(--money-green)', bg: 'var(--money-green-tint)' },
};

/** Reason callout tone — amber = a rule paused it, red = someone must act, else a neutral note. */
function reasonTone(v: ClVerdict): { fg: string; bg: string; bar: string } {
  if (v === 'held' || v === 'suppressed')
    return { fg: 'var(--warn-amber)', bg: 'var(--warn-amber-fill)', bar: 'var(--warn-amber)' };
  if (v === 'blocked' || v === 'failed')
    return { fg: 'var(--alert-red)', bg: 'var(--alert-red-tint)', bar: 'var(--alert-red)' };
  return { fg: 'var(--text-muted)', bg: 'var(--surface-sunken)', bar: 'var(--border-strong)' };
}

const VERDICTS: { id: ClVerdict; label: string }[] = [
  { id: 'sent', label: 'Sent' },
  { id: 'held', label: 'Held' },
  { id: 'skipped', label: 'Skipped' },
  { id: 'blocked', label: 'Blocked' },
  { id: 'suppressed', label: 'Suppressed' },
  { id: 'failed', label: 'Failed' },
];

function verdictPill(v: ClVerdict): { label: string; fg: string; bg: string } {
  switch (v) {
    case 'sent':
      return { label: 'Sent', fg: 'var(--text-secondary)', bg: 'var(--chip-fill)' };
    case 'held':
      return { label: 'Held', fg: 'var(--warn-amber)', bg: 'var(--warn-amber-fill)' };
    case 'suppressed':
      return { label: 'Suppressed', fg: 'var(--warn-amber)', bg: 'var(--warn-amber-fill)' };
    case 'skipped':
      return { label: 'Skipped', fg: 'var(--text-muted)', bg: 'var(--chip-fill)' };
    case 'blocked':
      return { label: 'Blocked', fg: 'var(--alert-red)', bg: 'var(--alert-red-fill)' };
    default:
      return { label: 'Failed', fg: 'var(--alert-red)', bg: 'var(--alert-red-fill)' };
  }
}

function outcomeColor(tone: ClRow['outcomeTone']): string {
  if (tone === 'good') return 'var(--money-green)';
  if (tone === 'alert') return 'var(--alert-red)';
  return 'var(--text-secondary)';
}

function fmtDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m)}:${s.toString().padStart(2, '0')}`;
}

function dayLabel(day: string): string {
  if (day === 'today') return 'Today';
  if (day === 'yesterday') return 'Yesterday';
  return day;
}

const COLS = 'grid-cols-[34px_minmax(0,1.1fr)_150px_minmax(0,1.5fr)_104px_minmax(0,1fr)_78px]';

const isChoseNot = (v: ClVerdict) => CL_CHOSE_NOT_TO_SEND.includes(v);

/**
 * An inbound row's chip is a disposition in the Dashboard's own colour, so the
 * chip the user lands on after clicking a Dashboard row is visibly the row they
 * clicked (§3.2).
 */
function dispositionPill(d: ClDisposition): { label: string; fg: string; bg: string } {
  const entry = CL_DISPOSITIONS.find((x) => x.id === d);
  const colour = entry?.colour ?? 'var(--text-muted)';
  return {
    label: entry?.label ?? '',
    fg: colour,
    bg: `color-mix(in srgb, ${colour} 12%, transparent)`,
  };
}

function Row({ r, onOpen }: { r: ClRow; onOpen?: () => void }) {
  const inbound = dirOf(r) === 'inbound';
  const pill = inbound && r.disposition !== undefined ? dispositionPill(r.disposition) : verdictPill(r.verdict);
  const chip = CHANNEL_CHIP[r.channel];
  const tone = reasonTone(r.verdict);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group block w-full border-t border-[var(--border-default)] px-3 py-[13px] text-left transition-colors hover:bg-[var(--surface-sunken)]"
    >
      <div className={cn('grid items-center gap-x-3 text-[13px] leading-[1.5]', COLS)}>
        <span className="flex justify-center">
          <span
            className="flex h-7 w-7 items-center justify-center rounded-full"
            style={{ background: chip.bg }}
          >
            <Icon kind={CHANNEL_ICON[r.channel]} size={14} stroke={2} style={{ color: chip.fg }} />
          </span>
        </span>
        {/* An unmatched caller renders the number and says so — never a blank, and
            never dropped from the list. A missing row and a matched row look
            identical to a reader (§4.1). */}
        <span className="min-w-0">
          {r.patient !== '' ? (
            <>
              <span className="block truncate font-semibold text-[var(--text-primary)]">{r.patient}</span>
              {r.callerNumber ? (
                <span className="block truncate font-[var(--font-mono)] text-[11px] text-[var(--text-muted)]">{r.callerNumber}</span>
              ) : null}
            </>
          ) : (
            <>
              <span className="block truncate font-[var(--font-mono)] font-semibold text-[var(--text-primary)]">
                {r.callerNumber ?? 'Unknown number'}
              </span>
              <span className="block text-[11px] text-[var(--text-muted)]">Not in the chart</span>
            </>
          )}
        </span>
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: AGENT_DOT[r.agent] }} />
          <span className="truncate text-[11.5px] text-[var(--text-secondary)]">{AGENT_LABEL[r.agent]}</span>
        </span>
        <span className="min-w-0">
          <span className="block truncate text-[var(--text-secondary)]">
            {r.purpose}
            {/* Which language it went in (amendment 25 §5). A practice that has
                switched a language on needs to see that the message really went
                in it, not that one was requested. */}
            {r.language !== undefined && r.language !== 'en' ? (
              <span className="ml-1.5 text-[11px] font-semibold text-[var(--brand-indigo)]">
                · {languageName(r.language)}
              </span>
            ) : null}
          </span>
          {r.channel === 'call' && r.durationSec !== undefined && r.durationSec > 0 ? (
            <span className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-[var(--text-muted)]">
              <Icon kind="play" size={9} stroke={2} style={{ color: 'var(--brand-indigo)' }} />
              <span className="font-[var(--font-mono)]">{fmtDuration(r.durationSec)}</span>
            </span>
          ) : null}
        </span>
        <span>
          <span
            className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold"
            style={{ color: pill.fg, background: pill.bg, borderColor: `color-mix(in srgb, ${pill.fg} 22%, transparent)` }}
          >
            {pill.label}
          </span>
        </span>
        <span className="min-w-0">
          <span className="block truncate font-semibold" style={{ color: outcomeColor(r.outcomeTone) }}>
            {r.outcome}
          </span>
          {/* The reason fits the existing cell — no new column (§7). This is what
              makes the Dashboard's reason bars clickable. */}
          {r.appointmentReason !== undefined ? (
            <span className="block truncate text-[11px] text-[var(--text-muted)]">{r.appointmentReason}</span>
          ) : null}
        </span>
        <span className="text-right font-[var(--font-mono)] text-[11px] text-[var(--text-muted)]">{r.time}</span>
      </div>
      {/* the reason — a full-width callout under the row; the most valuable text on the page */}
      {r.reason ? (
        <div
          className="ml-[46px] mr-[86px] mt-2 rounded-[var(--r-md)] px-2.5 py-1.5"
          style={{ background: tone.bg, borderLeft: `2px solid ${tone.bar}` }}
        >
          <span className="text-[11.5px] leading-[1.5]" style={{ color: tone.fg }}>
            {r.reason}
          </span>
        </div>
      ) : null}
    </button>
  );
}

export function CommLogView({ data, initialAgent, filters, onFiltersChange, empty }: CommLogViewProps) {
  // Uncontrolled unless the page hands filters down from the URL — so the
  // component still works in isolation (Storybook, a drawer) while the route it
  // ships on drives everything through the address bar.
  const [local, setLocal] = useState<ClFilters>({
    ...CL_DEFAULT_FILTERS,
    ...(initialAgent !== undefined ? { agent: initialAgent } : {}),
  });
  const f = reconcileFilters(filters ?? local);
  const setFilters = (next: ClFilters) => {
    const reconciled = reconcileFilters(next);
    if (onFiltersChange) onFiltersChange(reconciled);
    else setLocal(reconciled);
  };
  const set = <K extends keyof ClFilters>(key: K, value: ClFilters[K]) => {
    setFilters({ ...f, [key]: value });
  };

  const [selected, setSelected] = useState<ClRow | null>(null);

  const match = useMemo(() => {
    const q = f.query.trim().toLowerCase();
    return (
      r: ClRow,
      o?: {
        direction?: 'all' | ClDirection;
        agent?: 'all' | ClAgent;
        channel?: 'all' | ClChannel;
        verdict?: VerdictFilter;
      },
    ) => {
      const d = o?.direction ?? f.direction;
      const a = o?.agent ?? f.agent;
      const c = o?.channel ?? f.channel;
      const v = o?.verdict ?? f.verdict;
      if (d !== 'all' && dirOf(r) !== d) return false;
      if (a !== 'all' && r.agent !== a) return false;
      if (c !== 'all' && r.channel !== c) return false;
      // The two vocabularies never cross: an inbound row can only match a
      // disposition, an outbound row only a verdict.
      if (v !== 'all') {
        if (isDisposition(v)) {
          if (dirOf(r) !== 'inbound' || r.disposition !== v) return false;
        } else if (v === 'chose-not') {
          if (dirOf(r) !== 'outbound' || !isChoseNot(r.verdict)) return false;
        } else if (dirOf(r) !== 'outbound' || r.verdict !== v) return false;
      }
      if (f.reason !== 'all' && reasonSlug(r.appointmentReason ?? '') !== f.reason) return false;
      if (
        q !== '' &&
        !r.patient.toLowerCase().includes(q) &&
        !r.purpose.toLowerCase().includes(q) &&
        !(r.callerNumber ?? '').toLowerCase().includes(q)
      )
        return false;
      return true;
    };
  }, [f.direction, f.agent, f.channel, f.verdict, f.reason, f.query]);

  const rows = useMemo(() => data.rows.filter((r) => match(r)), [data.rows, match]);

  // Every count comes from the same query as the rows (§3.5, faceted).
  const scoped = useMemo(
    () => data.rows.filter((r) => match(r, { channel: 'all', verdict: 'all', direction: 'all' })),
    [data.rows, match],
  );
  const eyebrowTotal = scoped.length;
  const eyebrowInbound = scoped.filter((r) => dirOf(r) === 'inbound').length;
  const eyebrowHeld = scoped.filter((r) => dirOf(r) === 'outbound' && isChoseNot(r.verdict)).length;
  const directionCount = (d: 'all' | ClDirection) =>
    data.rows.filter((r) => match(r, { direction: d, verdict: 'all' })).length;
  const channelCount = (c: 'all' | ClChannel) =>
    data.rows.filter((r) => match(r, { channel: c, verdict: f.verdict })).length;
  const verdictCount = (v: VerdictFilter) =>
    data.rows.filter((r) => match(r, { verdict: v, channel: f.channel })).length;

  // Which verdict rows to show. Under `Everything` BOTH sets render, on two
  // labelled rows — never merged into one and never collapsed into a superset:
  // a `Sent` chip and a `Booked` chip are not alternatives to each other, and
  // putting them side by side implies they are (§3.3).
  const showOutbound = f.direction !== 'inbound';
  const showInbound = f.direction !== 'outbound';

  const csvHref = useMemo(() => toCsvHref(rows), [rows]);

  const channelChips: { id: 'all' | ClChannel; label: string }[] = [
    { id: 'all', label: 'Everything' },
    { id: 'text', label: 'Text' },
    { id: 'email', label: 'Email' },
    { id: 'call', label: 'Call' },
  ];

  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[1084px] flex-1 overflow-y-auto px-[30px] pb-16 pt-5">
      {/* header — no actions (this route declares none; §8.1) */}
      <div className="min-w-0">
        <div className="pc-eyebrow text-[var(--text-secondary)]">
          {eyebrowTotal} contacts · {eyebrowInbound} inbound · {eyebrowHeld} the agents chose not to send · all seven
          agents
        </div>
        <h1 className="m-0 mt-0.5 font-[var(--font-display)] text-[24px] font-semibold tracking-[-0.02em] text-[var(--text-primary)]">
          {data.titleLead} <span className="text-[var(--brand-indigo)]">{data.titleAccent}</span>
        </h1>
        <p className="m-0 mt-0.5 max-w-[680px] text-[12.5px] text-[var(--text-secondary)]">{data.subtitle}</p>
      </div>

      {/* filters — three axes */}
      <div className="mt-4 space-y-2">
        {/* agent select + search + date range */}
        <div className="flex flex-wrap items-center gap-2">
          <label className="sr-only" htmlFor="cl-agent">
            Agent
          </label>
          <select
            id="cl-agent"
            value={f.agent}
            onChange={(e) => { set('agent', e.target.value as 'all' | ClAgent); }}
            className="rounded-[var(--r-md)] border border-[var(--border-default)] bg-[var(--surface-card)] px-3 py-1.5 text-[12.5px] font-semibold text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
          >
            <option value="all">All agents</option>
            {AGENT_ORDER.map((a) => (
              <option key={a} value={a}>
                {AGENT_LABEL[a]}
              </option>
            ))}
          </select>
          {/* A real range, not a label. This page carries the flexibility precisely
              because the Dashboard deliberately has only two presets (§6). */}
          <span className="inline-flex items-center gap-1.5 rounded-[var(--r-md)] border border-[var(--border-default)] bg-[var(--surface-card)] px-2.5 py-1">
            <Icon kind="calendar" size={13} stroke={2} style={{ color: 'var(--text-muted)' }} />
            <label className="sr-only" htmlFor="cl-range">
              Date range
            </label>
            <select
              id="cl-range"
              value={f.range}
              onChange={(e) => { set('range', e.target.value as ClRange); }}
              className="bg-transparent text-[12.5px] font-semibold text-[var(--text-secondary)] outline-none"
            >
              {CL_RANGES.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
          </span>
          <div className="ml-auto flex items-center gap-2 rounded-[var(--r-md)] border border-[var(--border-default)] bg-[var(--surface-card)] px-3 py-1.5">
            <Icon kind="search" size={14} stroke={2} style={{ color: 'var(--text-muted)' }} />
            <input
              value={f.query}
              onChange={(e) => { set('query', e.target.value); }}
              placeholder="Patient, number, or purpose"
              className="w-[200px] bg-transparent text-[12.5px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)]"
            />
          </div>
          {/* Exports EXACTLY the filtered set — the same query as the rows and the
              counts (§3.5). This was going to be a Reports capability; with Reports
              gone it has to land here, and it is the only way to get raw records
              out of the product. */}
          <a
            href={csvHref}
            download="communication-log.csv"
            className="inline-flex items-center gap-1.5 rounded-[var(--r-md)] border border-[var(--border-default)] bg-[var(--surface-card)] px-3 py-1.5 text-[12.5px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]"
          >
            <Icon kind="download" size={13} stroke={2} style={{ color: 'var(--text-muted)' }} />
            Export {rows.length}
          </a>
        </div>

        {/* Direction comes FIRST, above channel, because it changes what the row
            below it means — and because "what calls came in yesterday" is the most
            common question a front desk has (§2). */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-[62px] shrink-0 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">
            Direction
          </span>
          <Chip label="Everything" count={directionCount('all')} on={f.direction === 'all'} onClick={() => { set('direction', 'all'); }} />
          <Chip label="Inbound" count={directionCount('inbound')} on={f.direction === 'inbound'} onClick={() => { set('direction', 'inbound'); }} />
          <Chip label="Outbound" count={directionCount('outbound')} on={f.direction === 'outbound'} onClick={() => { set('direction', 'outbound'); }} />
        </div>
        {/* channel chips */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-[52px] shrink-0 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">
            Channel
          </span>
          {channelChips.map((c) => (
            <Chip key={c.id} label={c.label} count={channelCount(c.id)} on={f.channel === c.id} onClick={() => { set('channel', c.id); }} />
          ))}
        </div>
        {/* The verdict row is direction-aware (§3). The six verdicts are OUTBOUND
            decisions and are meaningless for a contact the practice did not
            initiate — which is the whole reason the Carlos Reyes row read wrong. */}
        {showOutbound ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="w-[62px] shrink-0 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">
              {f.direction === 'all' ? 'Sent' : 'Verdict'}
            </span>
            {f.direction === 'outbound' ? (
              <Chip label="Everything" count={verdictCount('all')} on={f.verdict === 'all'} onClick={() => { set('verdict', 'all'); }} />
            ) : null}
            {VERDICTS.map((v) => (
              <Chip key={v.id} label={v.label} count={verdictCount(v.id)} on={f.verdict === v.id} onClick={() => { set('verdict', v.id); }} />
            ))}
            {/* Outbound-only, and its label already says so. It stays visible under
                `Everything`, counting only outbound rows (§3.4). */}
            <Chip
              label="Chose not to send"
              count={verdictCount('chose-not')}
              on={f.verdict === 'chose-not'}
              amber
              onClick={() => { set('verdict', 'chose-not'); }}
            />
          </div>
        ) : null}

        {showInbound ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="w-[62px] shrink-0 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">
              {f.direction === 'all' ? 'Came in' : 'What happened'}
            </span>
            {f.direction === 'inbound' ? (
              <Chip label="Everything" count={verdictCount('all')} on={f.verdict === 'all'} onClick={() => { set('verdict', 'all'); }} />
            ) : null}
            {CL_DISPOSITIONS.map((d) => (
              <Chip
                key={d.id}
                label={d.label}
                count={verdictCount(d.id)}
                on={f.verdict === d.id}
                onClick={() => { set('verdict', d.id); }}
              />
            ))}
          </div>
        ) : null}
      </div>

      {/* table */}
      <div className="mt-3 overflow-hidden rounded-[var(--r-xl)] border border-[var(--border-default)] bg-[var(--surface-card)] shadow-[var(--shadow-card)]">
        <div className={cn('grid items-center gap-x-3 border-b border-[var(--border-default)] bg-[var(--surface-header)] px-2 py-2.5 font-[var(--font-mono)] text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]', COLS)}>
          <span />
          <span>Patient</span>
          <span>Agent</span>
          <span>Purpose</span>
          <span>Verdict</span>
          <span>Outcome</span>
          <span className="text-right">Time</span>
        </div>
        {rows.length === 0 ? (
          <EmptyState state={empty ?? { state: 'zero' }} onClear={() => { setFilters(CL_DEFAULT_FILTERS); }} />
        ) : (
          rows.map((r, i) => {
            const showSep = i === 0 || rows[i - 1]?.day !== r.day;
            return (
              <React.Fragment key={r.id}>
                {showSep ? (
                  <div className="border-t border-[var(--border-default)] bg-[var(--surface-sunken)] px-3 py-1.5 font-[var(--font-mono)] text-[10px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">
                    {dayLabel(r.day)}
                  </div>
                ) : null}
                <Row r={r} onOpen={() => { setSelected(r); }} />
              </React.Fragment>
            );
          })
        )}
      </div>

      <CommLogDetailDrawer
        row={selected}
        onClose={() => {
          setSelected(null);
        }}
      />
    </div>
  );
}

/**
 * The three empty cases, visually distinct (§9). **A filter that matches nothing
 * and a query that could not run look identical unless we make them different** —
 * and they lead a reader to opposite conclusions about the product.
 */
function EmptyState({ state, onClear }: { state: ClEmptyState; onClear: () => void }) {
  if (state.state === 'not_set_up') {
    return (
      <div className="px-4 py-10 text-center">
        <div className="text-[13px] font-semibold text-[var(--text-primary)]">
          ELVA was not running yet on these dates.
        </div>
        <div className="mt-1 text-[12px] text-[var(--text-muted)]">
          There is nothing to show, and that is not the same as nothing having happened.
        </div>
      </div>
    );
  }
  if (state.state === 'unknown') {
    return (
      <div className="px-4 py-10 text-center">
        <div className="text-[13px] font-semibold text-[var(--text-primary)]">
          These contacts could not be read.
        </div>
        <div className="mt-1 text-[12px] text-[var(--text-muted)]">
          The sync is behind or the source did not answer — last good read at {state.lastGoodRead}.
        </div>
      </div>
    );
  }
  return (
    <div className="px-4 py-10 text-center">
      <div className="text-[12.5px] text-[var(--text-muted)]">No contacts match these filters.</div>
      <button
        type="button"
        onClick={onClear}
        className="mt-2 text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline"
      >
        Clear filters
      </button>
    </div>
  );
}

/**
 * The export (§8). **Exactly the filtered set** — the same rows the table is
 * showing, with the reason line, the direction and the full timestamp, so one CSV
 * row matches one header count.
 */
function toCsvHref(rows: ClRow[]): string {
  const head = [
    'direction',
    'time',
    'patient',
    'caller_number',
    'agent',
    'channel',
    'purpose',
    'verdict',
    'disposition',
    'appointment_reason',
    'outcome',
    'reason',
  ];
  const cell = (v: string | undefined) => `"${(v ?? '').replace(/"/g, '""')}"`;
  const body = rows.map((r) =>
    [
      dirOf(r),
      r.absoluteTime,
      r.patient,
      r.callerNumber,
      r.agent,
      r.channel,
      r.purpose,
      dirOf(r) === 'outbound' ? r.verdict : '',
      dirOf(r) === 'inbound' ? (r.disposition ?? '') : '',
      r.appointmentReason,
      r.outcome,
      r.reason,
    ]
      .map((v) => cell(v))
      .join(','),
  );
  const csv = [head.join(','), ...body].join('\n');
  return `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
}

function Chip({
  label,
  count,
  on,
  amber,
  onClick,
}: {
  label: string;
  count: number;
  on: boolean;
  amber?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-semibold',
        on
          ? amber
            ? 'border-[var(--warn-amber-line)] bg-[var(--warn-amber-fill)] text-[var(--warn-amber)]'
            : 'border-[var(--brand-indigo)] bg-[var(--brand-indigo)] text-[var(--text-on-accent)]'
          : 'border-[var(--border-default)] bg-[var(--surface-card)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]',
      )}
    >
      {label}
      <span className={cn('font-[var(--font-mono)] text-[11px] font-bold', on && !amber ? 'text-[var(--text-on-accent)]' : 'text-[var(--text-muted)]')}>
        {count}
      </span>
    </button>
  );
}
